import { useState } from "react";
import { useDialog } from "../hooks/useDialog";

const ROLES = {
  admin: {
    label: "Tournament Administrator",
    blurb: "Create and run the event",
    intro: "As an admin, you create the event, build the roster, and run the whole tournament from one dashboard — pools, brackets, and results all update live for everyone watching.",
    steps: [
      { title: "Create Your Event", desc: "Sign in and set up your tournament: name it, add disciplines like Longsword or Rapier, and get a shareable event code." },
      { title: "Build the Roster", desc: "Import fighters and judges from a spreadsheet, or add them by hand, then assign who's competing in what." },
      { title: "Run Pools & Brackets", desc: "Set up pools, assign refs and judges, and generate bouts. Once pools wrap up, seed and run the elimination bracket." },
      { title: "Share It Live", desc: "Everyone with the event code sees standings, brackets, and results update in real time." },
    ],
  },
  judge: {
    label: "Judge",
    blurb: "Score bouts from your phone",
    intro: "As a judge, you don't need an account to get started — just enter a bout code and start scoring.",
    steps: [
      { title: "Join With a Code", desc: "Enter the pool or bout code you were given. No sign-up required." },
      { title: "Score Each Round", desc: "Tap to count touches for each fighter, then submit your score to the ref." },
      { title: "Never Lose Your Place", desc: "If you refresh or lose connection mid-bout, your scores are saved — you'll pick up right where you left off." },
      { title: "Keep a History (Optional)", desc: "Register with your email to save your judging history across every event you work." },
    ],
  },
  competitor: {
    label: "Competitor",
    blurb: "Follow your bouts and results",
    intro: "As a competitor, you can follow your whole tournament — bouts, standings, and results — right from your phone.",
    steps: [
      { title: "Get Your Event Code", desc: "Your tournament organizer shares a code so you can follow along." },
      { title: "Watch Any Bout Live", desc: "Spectate matches, check pool standings, and track the bracket as it updates." },
      { title: "Claim Your Bouts", desc: "Register with the email your organizer has on file to link your name to your matches." },
      { title: "Review Your History", desc: "See every bout you've fought, across every event, from your account page." },
    ],
  },
};

export default function AboutModal({ onClose }) {
  const [role, setRole] = useState(null);
  const dialogRef = useDialog(onClose);

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center p-4"
      style={{ zIndex: 9999 }}
      onClick={onClose}
    >
      <div
        ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby="about-dialog-title" tabIndex={-1}
        className="bg-dark-800 border border-dark-500 rounded-2xl max-w-md w-full max-h-[85vh] overflow-y-auto p-6 relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          aria-label="Close about dialog"
          className="absolute top-4 right-4 min-w-8 min-h-8 text-gray-300 hover:text-white bg-transparent border-none cursor-pointer text-2xl leading-none"
        >
          &times;
        </button>

        {!role ? (
          <>
            <h2 id="about-dialog-title" className="font-display text-center text-lg font-bold text-white tracking-wide mb-1">
              WHAT IS JOY OF HEMA?
            </h2>
            <p className="text-gray-400 text-sm text-center mb-6">
              Pick what best describes you, and we'll walk through how you'll use the app.
            </p>
            <div className="flex flex-col gap-3">
              {Object.entries(ROLES).map(([key, r]) => (
                <button
                  key={key}
                  onClick={() => setRole(key)}
                  className="w-full p-4 bg-dark-700 border border-dark-500 rounded-xl text-left hover:border-gray-500 transition-colors cursor-pointer"
                >
                  <div className="font-display text-base font-bold text-white">{r.label}</div>
                  <div className="text-gray-500 text-xs mt-0.5">{r.blurb}</div>
                </button>
              ))}
            </div>
          </>
        ) : (
          <>
            <button
              onClick={() => setRole(null)}
              className="text-gray-500 hover:text-white bg-transparent border-none cursor-pointer text-xs font-display font-bold tracking-widest mb-4 p-0"
            >
              &larr; BACK
            </button>
            <h2 id="about-dialog-title" className="font-display text-center text-lg font-bold text-white tracking-wide mb-3">
              {ROLES[role].label.toUpperCase()}
            </h2>
            <p className="text-gray-300 text-sm text-center mb-6 leading-relaxed">{ROLES[role].intro}</p>
            <div className="text-center text-[10px] font-bold text-gray-500 tracking-widest mb-3">HOW IT WORKS</div>
            <div className="flex flex-col gap-3 mb-6">
              {ROLES[role].steps.map((s, i) => (
                <div key={i} className="bg-dark-700 border border-dark-500 rounded-xl p-4">
                  <div className="font-display text-sm font-bold text-white mb-1">{s.title}</div>
                  <div className="text-gray-400 text-xs leading-relaxed">{s.desc}</div>
                </div>
              ))}
            </div>
            <button
              onClick={onClose}
              className="w-full p-3.5 bg-gradient-to-br from-green-action-light to-green-action text-white font-display text-base font-bold rounded-xl tracking-wide border-none cursor-pointer"
            >
              Got It
            </button>
          </>
        )}
      </div>
    </div>
  );
}
